import {Box} from '@viptech/react-components'
import {useCallback, useEffect, useRef, useState} from 'react'
import {Coordinate} from 'src/services/api'
import {Dim} from '../../types/generics/ImageDetections'

type Props = {
  points?: Array<Coordinate>
  imageSrc: string
  width: number
  height: number
}

export type CoordinatesType = {x: number; y: number; reference: string}

export const DrawBarrierWithSvg = (props: Props) => {
  const {points, imageSrc, width, height} = props
  const imgRef = useRef<HTMLImageElement | null>(null)
  const [aspectRatio, setAspectRatio] = useState(1)
  const [_dimImage, setDimImage] = useState<Dim>({width: 0, height: 0})

  useEffect(() => {
    const updateSize = () => {
      if (imgRef.current) {
        const {clientWidth, clientHeight} = imgRef.current
        setDimImage({width: clientWidth, height: clientHeight})
        setAspectRatio(clientWidth / clientHeight)
      }
    }
    updateSize()
    window.addEventListener('resize', updateSize)
    return () => window.removeEventListener('resize', updateSize)
  }, [imgRef, imageSrc, width, height])

  const separatePointsByReference = useCallback(
    (points: {x: number; y: number; reference?: number}[]) => {
      return points.reduce(
        (acc, point) => {
          const ref = point.reference || 0
          if (!acc[ref]) acc[ref] = []
          acc[ref].push(point)
          return acc
        },
        {} as Record<number, typeof points>,
      )
    },
    [points],
  )

  const areaHasMultiplePoints = (points: Coordinate[]) => {
    return points.some((point) => point.reference && point.reference > 1)
  }

  return (
    <Box display='flex' align='center' justifyContent='center' width='100%' position='relative'>
      <img
        ref={imgRef}
        src={`data:image/png;base64,${imageSrc}`}
        alt='snapshot'
        width={width}
        height={height}
        style={{
          position: 'relative',
        }}
      />

      {points && !areaHasMultiplePoints(points) && (
        <svg style={{position: 'absolute', width, height, pointerEvents: 'none'}} xmlns='http://www.w3.org/2000/svg'>
          <polygon points={points.map((pt) => `${pt.x * _dimImage.width},${pt.y * _dimImage.height}`).join(' ')} fill='none' stroke='blue' strokeWidth='2' />
        </svg>
      )}

      {points &&
        areaHasMultiplePoints(points) &&
        Object.entries(separatePointsByReference(points)).map(([ref, groupedPoints]) => {
          return (
            <svg style={{position: 'absolute', width, height, pointerEvents: 'none'}} xmlns='http://www.w3.org/2000/svg' key={ref}>
              <polygon
                points={groupedPoints.map((pt) => `${pt.x * _dimImage.width},${pt.y * _dimImage.height}`).join(' ')}
                stroke='blue'
                fill='none'
                strokeWidth='2'
              />
            </svg>
          )
        })}
    </Box>
  )
}
